import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GeneralProfileNamePayload, GeneralProfileChatFlowProps } from './GeneralProfileChatFlow';
import { API_BASE_URL } from '../../../../../config';
import {
  disabilityFactsFromJson,
  disabilityFactsToJson,
  needsDisabilityFacts,
  type DisabilityCategory as SharedDisabilityCategory,
} from '../disabilityFacts';

export type Step =
  | 'legal'
  | 'kana'
  | 'dob'
  | 'gender'
  | 'nationality'
  | 'phone'
  | 'homePhone'
  | 'address'
  | 'email'
  | 'residenceCard'
  | 'myNumber'
  | 'maritalStatus'
  | 'familyStatus'
  | 'disabilityCategory'
  | 'disabilityFacts'
  | 'emergencyContact'
  | 'emergencyName'
  | 'emergencyRelationship'
  | 'emergencyPhone'
  | 'emergencyAddress'
  | 'finalConfirm';

export type MaritalStatus = 'married' | 'not_married';
export type FamilyStatusFlag = 'widowed' | 'single_parent';
export type DisabilityCategory = SharedDisabilityCategory;
export type YesNo = 'yes' | 'no';

const KANJI_OR_JP_CHARS_RE = /^[\u3040-\u309F\u30A0-\u30FF\u3400-\u9FFF\uF900-\uFAFF\u3000\sー]+$/u;
const BLOCK_CAPS_EN_RE = /^[A-Z][A-Z\s'-]*$/;
// Full-width + half-width Katakana, long vowel mark, spaces
const KATAKANA_RE = /^[\u30A0-\u30FF\uFF66-\uFF9D\u3000\sー]+$/u;

export function normalizeDigits(input: string) {
  return input.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
}

function normalizeFullWidthAlnum(input: string) {
  // Convert full-width 0-9A-Za-z into half-width.
  return input.replace(/[０-９Ａ-Ｚａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
}

export function normalizeJpPhone(input: string) {
  const half = normalizeDigits(input);
  return half.replace(/[^\d]/g, '');
}

export function normalizeResidenceCardNumber(input: string) {
  const half = normalizeFullWidthAlnum(input);
  return half.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function normalizeMyNumber(input: string) {
  return normalizeDigits(input).replace(/\D/g, '').slice(0, 12);
}

export function maskMyNumber(input: string) {
  const n = normalizeMyNumber(input);
  if (n.length < 4) return n;
  return `**** **** ${n.slice(-4)}`;
}

export function familySelectionsFromFlags(isWidowed: boolean, isSingleParent: boolean): FamilyStatusFlag[] {
  const selections: FamilyStatusFlag[] = [];
  if (isWidowed) selections.push('widowed');
  if (isSingleParent) selections.push('single_parent');
  return selections;
}

export function isJapaneseNationality(nationality: string): boolean {
  return nationality.trim() === 'Japanese';
}

export function hasStoredResidenceCard(residenceCardNumber: string): boolean {
  return normalizeResidenceCardNumber(residenceCardNumber).length > 0;
}

export function residenceCardRequired(nationality: string): boolean {
  return !isJapaneseNationality(nationality);
}

export { needsDisabilityFacts, disabilityFactsToJson, disabilityFactsFromJson };

function isResidenceCardInputCharsOnly(input: string) {
  // Allow: digits/letters (half/full), spaces, hyphen.
  return /^[0-9A-Za-z０-９Ａ-Ｚａ-ｚ\s-]+$/.test(input);
}

export function useGeneralProfileFlow({
  initialValues,
  currentEmail,
  onComplete,
  onProceedToSignature,
  t,
  wheelLocale,
}: {
  initialValues?: GeneralProfileChatFlowProps['initialValues'];
  currentEmail?: GeneralProfileChatFlowProps['currentEmail'];
  onComplete: GeneralProfileChatFlowProps['onComplete'];
  onProceedToSignature: GeneralProfileChatFlowProps['onProceedToSignature'];
  t: (key: string) => string;
  wheelLocale: string;
}) {
  const init = initialValues ?? {};
  const hasInitial = Boolean(
    (init.familyName && String(init.familyName).trim()) || (init.firstName && String(init.firstName).trim())
  );

  const [step, setStep] = useState<Step>(hasInitial ? 'kana' : 'legal');
  const [introRevealed, setIntroRevealed] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [editReturnStep, setEditReturnStep] = useState<Step | null>(null);

  const [legalFamily, setLegalFamily] = useState(init.familyName ?? '');
  const [legalFirst, setLegalFirst] = useState(init.firstName ?? '');
  const [legalMiddle, setLegalMiddle] = useState(init.middleName ?? '');

  const [kanaFamily, setKanaFamily] = useState(init.familyNameKana ?? '');
  const [kanaFirst, setKanaFirst] = useState(init.firstNameKana ?? '');
  const [kanaMiddle, setKanaMiddle] = useState(init.middleNameKana ?? '');

  const [dob, setDob] = useState(() => {
    const today = new Date();
    const fallback = { year: today.getFullYear() - 20, month: 1, day: 1 };
    const initDob = typeof (init as any).dateOfBirth === 'string' ? String((init as any).dateOfBirth) : '';
    const m = initDob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return fallback;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return fallback;
    return { year: y, month: mo, day: d };
  });

  const [legalCommitted, setLegalCommitted] = useState(hasInitial);
  const [kanaCommitted, setKanaCommitted] = useState(false);
  const [dobCommitted, setDobCommitted] = useState(false);

  const [gender, setGender] = useState<GeneralProfileNamePayload['gender']>((init as any).gender ?? 'unknown');
  const [genderCommitted, setGenderCommitted] = useState(false);

  const [nationality, setNationality] = useState<string>(
    typeof (init as any).nationality === 'string' ? String((init as any).nationality) : ''
  );
  const [nationalityCommitted, setNationalityCommitted] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState<string>(
    typeof (init as any).phoneNumber === 'string' ? String((init as any).phoneNumber) : ''
  );
  const [phoneCommitted, setPhoneCommitted] = useState(false);

  const [homePhoneNumber, setHomePhoneNumber] = useState<string>(
    typeof (init as any).homePhoneNumber === 'string' ? String((init as any).homePhoneNumber) : ''
  );
  const [homePhoneSkipped, setHomePhoneSkipped] = useState(false);
  const [homePhoneCommitted, setHomePhoneCommitted] = useState(false);

  const [address, setAddress] = useState(() => ({
    postalCode: typeof (init as any).postalCode === 'string' ? String((init as any).postalCode) : '',
    pref: typeof (init as any).addressPref === 'string' ? String((init as any).addressPref) : '',
    city: typeof (init as any).addressCity === 'string' ? String((init as any).addressCity) : '',
    town: typeof (init as any).addressTown === 'string' ? String((init as any).addressTown) : '',
    line2: typeof (init as any).addressLine2 === 'string' ? String((init as any).addressLine2) : '',
  }));
  const [addressCommitted, setAddressCommitted] = useState(false);

  const [email, setEmail] = useState<string>(() => {
    const initEmail = typeof (init as any).email === 'string' ? String((init as any).email) : '';
    const fallback = typeof currentEmail === 'string' ? String(currentEmail) : '';
    return initEmail || fallback || '';
  });
  const [emailCommitted, setEmailCommitted] = useState(false);

  const [residenceCardNumber, setResidenceCardNumber] = useState<string>(() => {
    const initN = typeof (init as any).residenceCardNumber === 'string' ? String((init as any).residenceCardNumber) : '';
    return initN || '';
  });
  const [residenceCardCommitted, setResidenceCardCommitted] = useState(false);

  const [myNumber, setMyNumber] = useState<string>(() => {
    const initN = typeof (init as any).myNumber === 'string' ? String((init as any).myNumber) : '';
    return initN || '';
  });
  const [myNumberCommitted, setMyNumberCommitted] = useState(false);
  const [myNumberSkipped, setMyNumberSkipped] = useState(false);

  const [maritalStatus, setMaritalStatus] = useState<MaritalStatus | ''>(() => {
    const v = (init as any).maritalStatus;
    return v === 'married' || v === 'not_married' ? v : '';
  });
  const [maritalStatusCommitted, setMaritalStatusCommitted] = useState(false);

  const [familyStatusSkipped, setFamilyStatusSkipped] = useState(false);
  const [familyStatusSelections, setFamilyStatusSelections] = useState<FamilyStatusFlag[]>([]);
  const [isWidowed, setIsWidowed] = useState(false);
  const [isSingleParent, setIsSingleParent] = useState(false);
  const [familyStatusCommitted, setFamilyStatusCommitted] = useState(false);

  const [disabilityCategory, setDisabilityCategory] = useState<DisabilityCategory | ''>(() => {
    const v = (init as any).disabilityCategory;
    return v === 'none' || v === 'general' || v === 'special' ? v : '';
  });
  const [disabilityCategoryCommitted, setDisabilityCategoryCommitted] = useState(false);
  const [disabilityFacts, setDisabilityFacts] = useState(() =>
    disabilityFactsFromJson(
      typeof (init as any).disabilityFactsJson === 'string'
        ? (init as any).disabilityFactsJson
        : typeof (init as any).disabilityFacts === 'string'
          ? (init as any).disabilityFacts
          : ''
    )
  );
  const [disabilityFactsCommitted, setDisabilityFactsCommitted] = useState(false);

  const [emergencyHasContact, setEmergencyHasContact] = useState<YesNo | ''>('');
  const [emergencyContactCommitted, setEmergencyContactCommitted] = useState(false);

  const [emergencyName, setEmergencyName] = useState<string>(() => {
    const v = typeof (init as any).emergencyContactName === 'string' ? String((init as any).emergencyContactName) : '';
    return v || '';
  });
  const [emergencyNameCommitted, setEmergencyNameCommitted] = useState(false);

  const [emRelationship, setEmRelationship] = useState<string>(
    typeof (init as any).emRelationship === 'string' ? String((init as any).emRelationship) : ''
  );
  const [emRelationshipCommitted, setEmRelationshipCommitted] = useState(false);

  const [emergencyPhone, setEmergencyPhone] = useState<string>(() => {
    const v =
      typeof (init as any).emergencyContactPhoneNumber === 'string'
        ? String((init as any).emergencyContactPhoneNumber)
        : '';
    return v || '';
  });
  const [emergencyPhoneCommitted, setEmergencyPhoneCommitted] = useState(false);

  const [emergencyAddress, setEmergencyAddress] = useState(() => ({
    postalCode:
      typeof (init as any).emergencyContactPostalCode === 'string'
        ? String((init as any).emergencyContactPostalCode)
        : '',
    pref:
      typeof (init as any).emergencyContactAddressPref === 'string'
        ? String((init as any).emergencyContactAddressPref)
        : '',
    city:
      typeof (init as any).emergencyContactAddressCity === 'string'
        ? String((init as any).emergencyContactAddressCity)
        : '',
    town:
      typeof (init as any).emergencyContactAddressTown === 'string'
        ? String((init as any).emergencyContactAddressTown)
        : '',
    line2:
      typeof (init as any).emergencyContactAddressLine2 === 'string'
        ? String((init as any).emergencyContactAddressLine2)
        : '',
  }));
  const [emergencyAddressCommitted, setEmergencyAddressCommitted] = useState(false);

  const stepOrder: Step[] = useMemo(
    () => [
      'legal',
      'kana',
      'dob',
      'gender',
      'nationality',
      'phone',
      'homePhone',
      'address',
      'email',
      'residenceCard',
      'myNumber',
      'maritalStatus',
      'familyStatus',
      'disabilityCategory',
      'disabilityFacts',
      'emergencyContact',
      'emergencyName',
      'emergencyRelationship',
      'emergencyPhone',
      'emergencyAddress',
      'finalConfirm',
    ],
    []
  );

  const stepIndex = useCallback((s: string | null | undefined) => {
    if (!s) return 0;
    const idx = stepOrder.indexOf(s as Step);
    return idx >= 0 ? idx : 0;
  }, [stepOrder]);

  const buildProgressValues = useCallback(() => {
    const dobLocal = `${String(dob.year).padStart(4, '0')}-${String(dob.month).padStart(2, '0')}-${String(dob.day).padStart(2, '0')}`;
    return {
      familyName: legalFamily.trim(),
      firstName: legalFirst.trim(),
      middleName: legalMiddle.trim(),
      familyNameKana: kanaFamily.trim(),
      firstNameKana: kanaFirst.trim(),
      middleNameKana: kanaMiddle.trim(),
      dateOfBirth: dobLocal,
      gender,
      nationality: nationality.trim(),
      phoneNumber: phoneNumber.trim(),
      homePhoneNumber: homePhoneNumber.trim(),

      postalCode: address.postalCode.trim(),
      prefecture: address.pref.trim(),
      city: address.city.trim(),
      streetAddress: address.town.trim(),
      buildingName: address.line2.trim(),
      roomNumber: '',

      emailAddress: email.trim(),
      residentCardNumber: normalizeResidenceCardNumber(residenceCardNumber),
      myNumber: normalizeMyNumber(myNumber),
      maritalStatus: maritalStatus || undefined,
      isWidowed,
      isSingleParent,
      isWorkingStudent: false,
      disabilityCategory: disabilityCategory || undefined,
      disabilityFactsJson: needsDisabilityFacts(disabilityCategory)
        ? disabilityFactsToJson(disabilityFacts)
        : '',

      emergencyContactName: emergencyName.trim(),
      emRelationship: emRelationship.trim(),
      emergencyContactPhone: emergencyPhone.trim(),
      emergencyPostalCode: emergencyAddress.postalCode.trim(),
      emergencyPrefecture: emergencyAddress.pref.trim(),
      emergencyCity: emergencyAddress.city.trim(),
      emergencyStreetAddress: emergencyAddress.town.trim(),
      emergencyBuildingName: emergencyAddress.line2.trim(),
      emergencyRoomNumber: '',
    };
  }, [
    address.city,
    address.line2,
    address.postalCode,
    address.pref,
    address.town,
    dob.day,
    dob.month,
    dob.year,
    email,
    emergencyAddress.city,
    emergencyAddress.line2,
    emergencyAddress.postalCode,
    emergencyAddress.pref,
    emergencyAddress.town,
    emergencyName,
    emergencyPhone,
    emRelationship,
    gender,
    kanaFamily,
    kanaFirst,
    kanaMiddle,
    legalFamily,
    legalFirst,
    legalMiddle,
    nationality,
    phoneNumber,
    homePhoneNumber,
    residenceCardNumber,
    myNumber,
    maritalStatus,
    isWidowed,
    isSingleParent,
    disabilityCategory,
    disabilityFacts,
  ]);

  const saveProgress = useCallback(
    async (nextStep: Step, status: 'notyet' | 'in_progress' | 'final_confirm' | 'completed' = 'in_progress') => {
      if (!currentEmail) return;
      if (!API_BASE_URL) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/general-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: currentEmail,
            status,
            step: nextStep,
            values: buildProgressValues(),
          }),
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (e) {
        // Non-blocking; user can continue even if autosave fails temporarily.
        console.warn('Failed to save general profile progress', e);
      }
    },
    [buildProgressValues, currentEmail]
  );

  const beginEdit = useCallback(
    (
      target: Exclude<
        Step,
        'finalConfirm'
      >
    ) => {
      if (target === 'familyStatus') {
        setFamilyStatusSkipped(false);
        setFamilyStatusSelections([]);
      }
      if (target === 'myNumber') {
        setMyNumberSkipped(false);
      }
      if (target === 'homePhone') {
        setHomePhoneSkipped(false);
      }
      // When editing from the final review, jump back to the review after saving one answer.
      setEditReturnStep(step === 'finalConfirm' ? 'finalConfirm' : null);
      setStep(target);
    },
    [step]
  );

  const maybeReturnAfterEdit = useCallback(() => {
    if (!editReturnStep) return false;
    const ret = editReturnStep;
    setEditReturnStep(null);
    setStep(ret);
    void saveProgress(ret, 'completed');
    return true;
  }, [editReturnStep, saveProgress]);

  useEffect(() => {
    if (!currentEmail) return;
    if (!API_BASE_URL) return;
    let cancelled = false;
    const ctrl = new AbortController();

    (async () => {
      setIsLoadingProgress(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/general-profile?email=${encodeURIComponent(currentEmail)}`, {
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as any;
        if (cancelled) return;
        const v = (data && data.values) || {};

        // Values
        setLegalFamily(typeof v.familyName === 'string' ? v.familyName : '');
        setLegalFirst(typeof v.firstName === 'string' ? v.firstName : '');
        setLegalMiddle(typeof v.middleName === 'string' ? v.middleName : '');

        setKanaFamily(typeof v.familyNameKana === 'string' ? v.familyNameKana : '');
        setKanaFirst(typeof v.firstNameKana === 'string' ? v.firstNameKana : '');
        setKanaMiddle(typeof v.middleNameKana === 'string' ? v.middleNameKana : '');

        if (typeof v.dateOfBirth === 'string') {
          const m = v.dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (m) {
            setDob({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) });
          }
        }

        if (typeof v.gender === 'string') setGender(v.gender);
        if (typeof v.nationality === 'string') setNationality(v.nationality);
        if (typeof v.phoneNumber === 'string') setPhoneNumber(v.phoneNumber);
        const loadedHomePhone = typeof v.homePhoneNumber === 'string' ? v.homePhoneNumber.trim() : '';
        if (typeof v.homePhoneNumber === 'string') setHomePhoneNumber(v.homePhoneNumber);

        setAddress({
          postalCode: typeof v.postalCode === 'string' ? v.postalCode : '',
          pref: typeof v.prefecture === 'string' ? v.prefecture : '',
          city: typeof v.city === 'string' ? v.city : '',
          town: typeof v.streetAddress === 'string' ? v.streetAddress : '',
          line2: typeof v.buildingName === 'string' ? v.buildingName : '',
        });

        if (typeof v.emailAddress === 'string') setEmail(v.emailAddress);
        if (typeof v.residentCardNumber === 'string') setResidenceCardNumber(v.residentCardNumber);
        if (typeof v.myNumber === 'string') setMyNumber(v.myNumber);
        const loadedNationality = typeof v.nationality === 'string' ? v.nationality : '';
        const loadedResidenceCard =
          typeof v.residentCardNumber === 'string' ? normalizeResidenceCardNumber(v.residentCardNumber) : '';
        const loadedMyNumber = typeof v.myNumber === 'string' ? normalizeMyNumber(v.myNumber) : '';
        if (v.maritalStatus === 'married' || v.maritalStatus === 'not_married') {
          setMaritalStatus(v.maritalStatus);
        }
        const loadedWidowed = Boolean(v.isWidowed);
        const loadedSingleParent = Boolean(v.isSingleParent);
        setIsWidowed(loadedWidowed);
        setIsSingleParent(loadedSingleParent);
        if (v.disabilityCategory === 'none' || v.disabilityCategory === 'general' || v.disabilityCategory === 'special') {
          setDisabilityCategory(v.disabilityCategory);
        }
        if (typeof v.disabilityFactsJson === 'string') {
          setDisabilityFacts(disabilityFactsFromJson(v.disabilityFactsJson));
        }

        if (typeof v.emergencyContactName === 'string') setEmergencyName(v.emergencyContactName);
        if (typeof v.emRelationship === 'string') setEmRelationship(v.emRelationship);
        if (typeof v.emergencyContactPhone === 'string') setEmergencyPhone(v.emergencyContactPhone);

        setEmergencyAddress({
          postalCode: typeof v.emergencyPostalCode === 'string' ? v.emergencyPostalCode : '',
          pref: typeof v.emergencyPrefecture === 'string' ? v.emergencyPrefecture : '',
          city: typeof v.emergencyCity === 'string' ? v.emergencyCity : '',
          town: typeof v.emergencyStreetAddress === 'string' ? v.emergencyStreetAddress : '',
          line2: typeof v.emergencyBuildingName === 'string' ? v.emergencyBuildingName : '',
        });

        // Commit flags + step cursor
        const idx = stepIndex(typeof data?.step === 'string' ? data.step : 'legal');
        setLegalCommitted(idx > stepIndex('legal'));
        setKanaCommitted(idx > stepIndex('kana'));
        setDobCommitted(idx > stepIndex('dob'));
        setGenderCommitted(idx > stepIndex('gender'));
        setNationalityCommitted(idx > stepIndex('nationality'));
        setPhoneCommitted(idx > stepIndex('phone'));
        if (idx > stepIndex('homePhone') && !loadedHomePhone) {
          setHomePhoneSkipped(true);
          setHomePhoneCommitted(true);
        } else {
          setHomePhoneSkipped(false);
          setHomePhoneCommitted(idx > stepIndex('homePhone'));
        }
        setAddressCommitted(idx > stepIndex('address'));
        setEmailCommitted(idx > stepIndex('email'));
        if (
          !residenceCardRequired(loadedNationality) &&
          !hasStoredResidenceCard(loadedResidenceCard) &&
          idx > stepIndex('email')
        ) {
          setResidenceCardCommitted(true);
        } else {
          setResidenceCardCommitted(idx > stepIndex('residenceCard'));
        }
        if (idx > stepIndex('myNumber') && !loadedMyNumber) {
          setMyNumberSkipped(true);
          setMyNumberCommitted(true);
        } else {
          setMyNumberSkipped(false);
          setMyNumberCommitted(idx > stepIndex('myNumber'));
        }
        setMaritalStatusCommitted(idx > stepIndex('maritalStatus'));
        const familyIdx = stepIndex('familyStatus');
        const familyDone = idx > familyIdx;
        setFamilyStatusCommitted(familyDone);
        if (familyDone) {
          const skipped = !loadedWidowed && !loadedSingleParent;
          setFamilyStatusSkipped(skipped);
          setFamilyStatusSelections(
            skipped ? [] : familySelectionsFromFlags(loadedWidowed, loadedSingleParent)
          );
        }
        setDisabilityCategoryCommitted(idx > stepIndex('disabilityCategory'));
        const loadedDisabilityCategory =
          v.disabilityCategory === 'none' || v.disabilityCategory === 'general' || v.disabilityCategory === 'special'
            ? v.disabilityCategory
            : '';
        if (needsDisabilityFacts(loadedDisabilityCategory)) {
          setDisabilityFactsCommitted(idx > stepIndex('disabilityFacts'));
        } else {
          setDisabilityFactsCommitted(false);
        }

        const nextStep = (typeof data?.step === 'string' ? data.step : 'legal') as Step;
        const loadedEmergencyName =
          typeof v.emergencyContactName === 'string' ? v.emergencyContactName.trim() : '';
        const onEmergencyDetailStep =
          nextStep === 'emergencyName' ||
          nextStep === 'emergencyRelationship' ||
          nextStep === 'emergencyPhone' ||
          nextStep === 'emergencyAddress';
        const pastEmergencyGate = idx > stepIndex('emergencyContact');
        if (pastEmergencyGate || onEmergencyDetailStep) {
          if (onEmergencyDetailStep || loadedEmergencyName) {
            setEmergencyHasContact('yes');
            setEmergencyContactCommitted(true);
          } else {
            setEmergencyHasContact('no');
            setEmergencyContactCommitted(true);
          }
        } else {
          setEmergencyHasContact('');
          setEmergencyContactCommitted(false);
        }

        setEmergencyNameCommitted(idx > stepIndex('emergencyName'));
        setEmRelationshipCommitted(idx > stepIndex('emergencyRelationship'));
        setEmergencyPhoneCommitted(idx > stepIndex('emergencyPhone'));
        setEmergencyAddressCommitted(idx > stepIndex('emergencyAddress'));

        if (stepOrder.includes(nextStep)) {
          if (!residenceCardRequired(loadedNationality) && nextStep === 'residenceCard') {
            setStep('myNumber');
          } else {
            setStep(nextStep);
          }
        }
      } catch {
        // ignore load failures (treat as fresh start)
      } finally {
        if (!cancelled) setIsLoadingProgress(false);
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [currentEmail, stepIndex, stepOrder]);

  const scrollToEnd = useCallback(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  useEffect(() => {
    setIntroRevealed(0);
    const id = window.setTimeout(() => setIntroRevealed(1), 150);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    scrollToEnd();
  }, [
    introRevealed,
    step,
    legalCommitted,
    kanaCommitted,
    dobCommitted,
    genderCommitted,
    nationalityCommitted,
    phoneCommitted,
    homePhoneCommitted,
    addressCommitted,
    emailCommitted,
    residenceCardCommitted,
    myNumberCommitted,
    maritalStatusCommitted,
    familyStatusCommitted,
    disabilityCategoryCommitted,
    disabilityFactsCommitted,
    emergencyContactCommitted,
    emergencyNameCommitted,
    emRelationshipCommitted,
    emergencyPhoneCommitted,
    emergencyAddressCommitted,
    scrollToEnd,
  ]);

  const canSendLegal = legalFamily.trim().length > 0 && legalFirst.trim().length > 0;
  const canSendKana = kanaFamily.trim().length > 0 && kanaFirst.trim().length > 0;

  const validateLegalName = useCallback(
    (v: string) => {
      const s = v.trim();
      if (s.length === 0) return null;
      if (BLOCK_CAPS_EN_RE.test(s)) return null;
      if (KANJI_OR_JP_CHARS_RE.test(s)) return null;
      return t('submitDocuments.profileName.validation.legal');
    },
    [t]
  );

  const validateKana = useCallback(
    (v: string) => {
      const s = v.trim();
      if (s.length === 0) return null;
      if (KATAKANA_RE.test(s)) return null;
      return t('submitDocuments.profileName.validation.kana');
    },
    [t]
  );

  const validatePhone = useCallback(
    (v: string) => {
      const s = v.trim();
      if (s.length === 0) return null;
      const digits = normalizeJpPhone(s);
      if ((digits.length === 10 || digits.length === 11) && digits.startsWith('0')) return null;
      return t('submitDocuments.profileName.validation.phone');
    },
    [t]
  );

  const validateEmail = useCallback(
    (v: string) => {
      const s = v.trim();
      if (s.length === 0) return null;
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return null;
      return t('submitDocuments.profileName.validation.email');
    },
    [t]
  );

  const validateResidenceCardNumber = useCallback(
    (v: string) => {
      const s = v.trim();
      if (s.length === 0) return null;
      if (!isResidenceCardInputCharsOnly(s)) {
        return t('submitDocuments.profileName.validation.residenceCardNumber');
      }
      const code = normalizeResidenceCardNumber(s);
      if (/^[A-Z0-9]{12}$/.test(code)) return null;
      return t('submitDocuments.profileName.validation.residenceCardNumber');
    },
    [t]
  );

  const validateMyNumber = useCallback(
    (v: string) => {
      const s = v.trim();
      if (s.length === 0) return null;
      const code = normalizeMyNumber(s);
      if (/^\d{12}$/.test(code)) return null;
      return t('submitDocuments.profileName.validation.myNumber');
    },
    [t]
  );

  const handleSendLegal = () => {
    if (!canSendLegal) return;
    setLegalCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('kana');
    setKanaFamily('');
    setKanaFirst('');
    setKanaMiddle('');
    void saveProgress('kana', 'in_progress');
  };

  const handleSendKana = () => {
    if (!canSendKana) return;
    setKanaCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('dob');
    void saveProgress('dob', 'in_progress');
  };

  const dobString = `${String(dob.year).padStart(4, '0')}-${String(dob.month).padStart(2, '0')}-${String(dob.day).padStart(2, '0')}`;

  const dobDisplayFormatted = useMemo(() => {
    const d = new Date(dob.year, dob.month - 1, dob.day);
    return new Intl.DateTimeFormat(wheelLocale, { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
  }, [dob.year, dob.month, dob.day, wheelLocale]);

  const handleSendDob = () => {
    setDobCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('gender');
    void saveProgress('gender', 'in_progress');
  };

  const canSendGender = gender !== 'unknown';

  const handleSendGender = () => {
    if (!canSendGender) return;
    setGenderCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('nationality');
    void saveProgress('nationality', 'in_progress');
  };

  const advanceToPostEmailStep = useCallback(() => {
    if (residenceCardRequired(nationality)) {
      setStep('residenceCard');
      void saveProgress('residenceCard', 'in_progress');
      return;
    }
    setResidenceCardNumber('');
    setResidenceCardCommitted(true);
    setStep('myNumber');
    void saveProgress('myNumber', 'in_progress');
  }, [nationality, saveProgress]);

  const handleSendNationality = () => {
    const n = nationality.trim();
    if (!n) return;
    setNationalityCommitted(true);

    if (editReturnStep) {
      if (isJapaneseNationality(n)) {
        setResidenceCardNumber('');
        setResidenceCardCommitted(true);
      } else if (!hasStoredResidenceCard(residenceCardNumber)) {
        setResidenceCardCommitted(false);
        setStep('residenceCard');
        void saveProgress('residenceCard', 'in_progress');
        return;
      }
      maybeReturnAfterEdit();
      return;
    }

    if (isJapaneseNationality(n)) {
      setResidenceCardNumber('');
    }
    setStep('phone');
    void saveProgress('phone', 'in_progress');
  };

  const canSendPhone = phoneNumber.trim().length > 0 && validatePhone(phoneNumber) == null;

  const handleSendPhone = () => {
    if (!canSendPhone) return;
    setPhoneCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('homePhone');
    void saveProgress('homePhone', 'in_progress');
  };

  const canSendHomePhone = homePhoneNumber.trim().length > 0 && validatePhone(homePhoneNumber) == null;

  const handleSendHomePhone = () => {
    if (!canSendHomePhone) return;
    setHomePhoneSkipped(false);
    setHomePhoneCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('address');
    void saveProgress('address', 'in_progress');
  };

  const handleSkipHomePhone = () => {
    setHomePhoneNumber('');
    setHomePhoneSkipped(true);
    setHomePhoneCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('address');
    void saveProgress('address', 'in_progress');
  };

  const canSendAddress =
    address.postalCode.trim().length > 0 &&
    address.pref.trim().length > 0 &&
    address.city.trim().length > 0 &&
    address.town.trim().length > 0;

  const handleSendAddress = () => {
    if (!canSendAddress) return;
    setAddressCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('email');
    void saveProgress('email', 'in_progress');
  };

  const canSendEmail = email.trim().length > 0 && validateEmail(email) == null;

  const handleSendEmail = () => {
    if (!canSendEmail) return;
    setEmailCommitted(true);
    if (maybeReturnAfterEdit()) return;
    advanceToPostEmailStep();
  };

  const canSendResidenceCard =
    residenceCardNumber.trim().length > 0 && validateResidenceCardNumber(residenceCardNumber) == null;

  const handleSendResidenceCard = () => {
    if (!canSendResidenceCard) return;
    setResidenceCardCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('myNumber');
    void saveProgress('myNumber', 'in_progress');
  };

  const canSendMyNumber = myNumber.trim().length > 0 && validateMyNumber(myNumber) == null;

  const handleSendMyNumber = () => {
    if (!canSendMyNumber) return;
    setMyNumber(normalizeMyNumber(myNumber));
    setMyNumberSkipped(false);
    setMyNumberCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('maritalStatus');
    void saveProgress('maritalStatus', 'in_progress');
  };

  const handleSkipMyNumber = () => {
    setMyNumber('');
    setMyNumberSkipped(true);
    setMyNumberCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('maritalStatus');
    void saveProgress('maritalStatus', 'in_progress');
  };

  const canSendMaritalStatus = maritalStatus === 'married' || maritalStatus === 'not_married';

  const handleSendMaritalStatus = () => {
    if (!canSendMaritalStatus) return;
    if (maritalStatus === 'not_married') {
      setIsWidowed(false);
      setFamilyStatusSelections((prev) => prev.filter((f) => f !== 'widowed'));
    }
    setMaritalStatusCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setFamilyStatusSkipped(false);
    setFamilyStatusSelections([]);
    setStep('familyStatus');
    void saveProgress('familyStatus', 'in_progress');
  };

  const canSendFamilyStatus = familyStatusSkipped || familyStatusSelections.length > 0;

  const handleSendFamilyStatus = () => {
    if (!canSendFamilyStatus) return;
    if (familyStatusSkipped) {
      setIsWidowed(false);
      setIsSingleParent(false);
    } else {
      setIsWidowed(familyStatusSelections.includes('widowed'));
      setIsSingleParent(familyStatusSelections.includes('single_parent'));
    }
    setFamilyStatusCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('disabilityCategory');
    void saveProgress('disabilityCategory', 'in_progress');
  };

  const canSendDisabilityCategory =
    disabilityCategory === 'none' || disabilityCategory === 'general' || disabilityCategory === 'special';

  const handleSendDisabilityCategory = () => {
    if (!canSendDisabilityCategory) return;
    setDisabilityCategoryCommitted(true);
    if (!needsDisabilityFacts(disabilityCategory)) {
      setDisabilityFacts('');
      setDisabilityFactsCommitted(false);
      if (maybeReturnAfterEdit()) return;
      setStep('emergencyContact');
      void saveProgress('emergencyContact', 'in_progress');
      return;
    }
    if (editReturnStep && disabilityFacts.trim()) {
      setDisabilityFactsCommitted(true);
      if (maybeReturnAfterEdit()) return;
    }
    setEditReturnStep(null);
    setDisabilityFactsCommitted(false);
    setStep('disabilityFacts');
    void saveProgress('disabilityFacts', 'in_progress');
  };

  const canSendDisabilityFacts = disabilityFacts.trim().length > 0;

  const handleSendDisabilityFacts = () => {
    if (!canSendDisabilityFacts) return;
    setDisabilityFactsCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('emergencyContact');
    void saveProgress('emergencyContact', 'in_progress');
  };

  const canSendEmergencyContact = emergencyHasContact === 'yes' || emergencyHasContact === 'no';

  const clearEmergencyFields = () => {
    setEmergencyName('');
    setEmRelationship('');
    setEmergencyPhone('');
    setEmergencyAddress({ postalCode: '', pref: '', city: '', town: '', line2: '' });
  };

  const handleSendEmergencyContact = () => {
    if (!canSendEmergencyContact) return;
    setEmergencyContactCommitted(true);
    if (emergencyHasContact === 'no') {
      clearEmergencyFields();
      setEmergencyNameCommitted(true);
      setEmRelationshipCommitted(true);
      setEmergencyPhoneCommitted(true);
      setEmergencyAddressCommitted(true);
      if (maybeReturnAfterEdit()) return;
      setStep('finalConfirm');
      void saveProgress('finalConfirm', 'final_confirm');
      return;
    }
    setEmergencyNameCommitted(false);
    setEmRelationshipCommitted(false);
    setEmergencyPhoneCommitted(false);
    setEmergencyAddressCommitted(false);
    // Switching to Yes requires walking through contact fields (do not jump back to final confirm).
    setEditReturnStep(null);
    setStep('emergencyName');
    void saveProgress('emergencyName', 'in_progress');
  };

  const canSendEmergencyName = emergencyName.trim().length > 0;

  const handleSendEmergencyName = () => {
    if (!canSendEmergencyName) return;
    setEmergencyNameCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('emergencyRelationship');
    void saveProgress('emergencyRelationship', 'in_progress');
  };

  const canSendEmergencyRelationship = emRelationship.trim().length > 0;

  const handleSendEmergencyRelationship = () => {
    if (!canSendEmergencyRelationship) return;
    setEmRelationshipCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('emergencyPhone');
    void saveProgress('emergencyPhone', 'in_progress');
  };

  const canSendEmergencyPhone = emergencyPhone.trim().length > 0 && validatePhone(emergencyPhone) == null;

  const handleSendEmergencyPhone = () => {
    if (!canSendEmergencyPhone) return;
    setEmergencyPhoneCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('emergencyAddress');
    void saveProgress('emergencyAddress', 'in_progress');
  };

  const canSendEmergencyAddress =
    emergencyAddress.postalCode.trim().length > 0 &&
    emergencyAddress.pref.trim().length > 0 &&
    emergencyAddress.city.trim().length > 0 &&
    emergencyAddress.town.trim().length > 0;

  const handleSendEmergencyAddress = () => {
    if (!canSendEmergencyAddress) return;
    setEmergencyAddressCommitted(true);
    if (maybeReturnAfterEdit()) return;
    setStep('finalConfirm');
    void saveProgress('finalConfirm', 'final_confirm');
  };

  const buildNamePayload = useCallback((): GeneralProfileNamePayload => {
    return {
      familyName: legalFamily.trim(),
      firstName: legalFirst.trim(),
      middleName: legalMiddle.trim(),
      familyNameKana: kanaFamily.trim(),
      firstNameKana: kanaFirst.trim(),
      middleNameKana: kanaMiddle.trim(),
      dateOfBirth: dobString,
      gender,
      nationality: nationality.trim(),
      phoneNumber: phoneNumber.trim(),
      homePhoneNumber: homePhoneNumber.trim(),
      postalCode: address.postalCode.trim(),
      addressPref: address.pref.trim(),
      addressCity: address.city.trim(),
      addressTown: address.town.trim(),
      addressLine2: address.line2.trim(),
      email: email.trim(),
      residenceCardNumber: normalizeResidenceCardNumber(residenceCardNumber),
      myNumber: normalizeMyNumber(myNumber),
      maritalStatus: maritalStatus || undefined,
      isWidowed,
      isSingleParent,
      isWorkingStudent: false,
      disabilityCategory: disabilityCategory || undefined,
      disabilityFacts: disabilityFacts.trim(),
      disabilityFactsJson: needsDisabilityFacts(disabilityCategory)
        ? disabilityFactsToJson(disabilityFacts)
        : '',
      emergencyContactName: emergencyName.trim(),
      emRelationship: emRelationship.trim(),
      emergencyContactPhoneNumber: emergencyPhone.trim(),
      emergencyContactPostalCode: emergencyAddress.postalCode.trim(),
      emergencyContactAddressPref: emergencyAddress.pref.trim(),
      emergencyContactAddressCity: emergencyAddress.city.trim(),
      emergencyContactAddressTown: emergencyAddress.town.trim(),
      emergencyContactAddressLine2: emergencyAddress.line2.trim(),
    };
  }, [
    address.city,
    address.line2,
    address.postalCode,
    address.pref,
    address.town,
    dobString,
    email,
    emergencyAddress.city,
    emergencyAddress.line2,
    emergencyAddress.postalCode,
    emergencyAddress.pref,
    emergencyAddress.town,
    emergencyName,
    emergencyPhone,
    gender,
    kanaFamily,
    kanaFirst,
    kanaMiddle,
    legalFamily,
    legalFirst,
    legalMiddle,
    nationality,
    phoneNumber,
    homePhoneNumber,
    emRelationship,
    residenceCardNumber,
    myNumber,
    maritalStatus,
    isWidowed,
    isSingleParent,
    disabilityCategory,
    disabilityFacts,
  ]);

  const handleProceedToSignature = () => {
    void saveProgress('finalConfirm', 'final_confirm');
    onProceedToSignature(buildNamePayload());
  };

  const handleContinueAfterFinalConfirm = () => {
    void saveProgress('finalConfirm', 'completed');
    onComplete(buildNamePayload());
  };

  const handleUndo = () => {
    if (step === 'kana' && legalCommitted) {
      setLegalCommitted(false);
      setStep('legal');
      return;
    }
    if (step === 'dob' && kanaCommitted) {
      setKanaCommitted(false);
      setStep('kana');
      return;
    }
    if (step === 'gender' && dobCommitted) {
      setDobCommitted(false);
      setStep('dob');
      return;
    }
    if (step === 'nationality' && genderCommitted) {
      setGenderCommitted(false);
      setStep('gender');
      return;
    }
    if (step === 'phone' && nationalityCommitted) {
      setNationalityCommitted(false);
      setStep('nationality');
      return;
    }
    if (step === 'homePhone' && phoneCommitted) {
      setPhoneCommitted(false);
      setStep('phone');
      return;
    }
    if (step === 'address' && homePhoneCommitted) {
      setHomePhoneCommitted(false);
      setStep('homePhone');
      return;
    }
    if (step === 'email' && addressCommitted) {
      setAddressCommitted(false);
      setStep('address');
      return;
    }
    if (step === 'residenceCard' && emailCommitted) {
      setEmailCommitted(false);
      setStep('email');
      return;
    }
    if (step === 'myNumber' && residenceCardCommitted) {
      setMyNumberCommitted(false);
      setMyNumberSkipped(false);
      if (residenceCardRequired(nationality)) {
        setResidenceCardCommitted(false);
        setStep('residenceCard');
      } else {
        setResidenceCardCommitted(false);
        setStep('email');
      }
      return;
    }
    if (step === 'maritalStatus' && myNumberCommitted) {
      setMyNumberCommitted(false);
      setStep('myNumber');
      return;
    }
    if (step === 'familyStatus' && maritalStatusCommitted) {
      setMaritalStatusCommitted(false);
      setStep('maritalStatus');
      return;
    }
    if (step === 'disabilityCategory' && familyStatusCommitted) {
      setFamilyStatusCommitted(false);
      setFamilyStatusSkipped(false);
      setFamilyStatusSelections([]);
      setStep('familyStatus');
      return;
    }
    if (step === 'disabilityFacts' && disabilityCategoryCommitted) {
      setDisabilityFactsCommitted(false);
      setDisabilityCategoryCommitted(false);
      setStep('disabilityCategory');
      return;
    }
    if (step === 'emergencyContact' && disabilityCategoryCommitted) {
      if (needsDisabilityFacts(disabilityCategory) && disabilityFactsCommitted) {
        setDisabilityFactsCommitted(false);
        setStep('disabilityFacts');
        return;
      }
      setDisabilityCategoryCommitted(false);
      setStep('disabilityCategory');
      return;
    }
    if (step === 'emergencyName' && emergencyContactCommitted) {
      setEmergencyContactCommitted(false);
      setStep('emergencyContact');
      return;
    }
    if (step === 'emergencyRelationship' && emergencyNameCommitted) {
      setEmergencyNameCommitted(false);
      setStep('emergencyName');
      return;
    }
    if (step === 'emergencyPhone' && emRelationshipCommitted) {
      setEmRelationshipCommitted(false);
      setStep('emergencyRelationship');
      return;
    }
    if (step === 'emergencyAddress' && emergencyPhoneCommitted) {
      setEmergencyPhoneCommitted(false);
      setStep('emergencyPhone');
      return;
    }
    if (step === 'finalConfirm' && emergencyContactCommitted) {
      if (emergencyHasContact === 'no') {
        setEmergencyContactCommitted(false);
        setEmergencyNameCommitted(false);
        setEmRelationshipCommitted(false);
        setEmergencyPhoneCommitted(false);
        setEmergencyAddressCommitted(false);
        setStep('emergencyContact');
        return;
      }
      if (emergencyAddressCommitted) {
        setEmergencyAddressCommitted(false);
        setStep('emergencyAddress');
        return;
      }
    }
  };

  const showUndoLegal = step === 'kana' && legalCommitted;
  const showUndoKana = step === 'dob' && kanaCommitted && !dobCommitted;
  const showUndoDob = step === 'gender' && dobCommitted && !genderCommitted;
  const showUndoGender = step === 'nationality' && genderCommitted && !nationalityCommitted;
  const showUndoNationality = step === 'phone' && nationalityCommitted && !phoneCommitted;
  const showUndoPhone = step === 'homePhone' && phoneCommitted && !homePhoneCommitted;
  const showUndoHomePhone = step === 'address' && homePhoneCommitted && !addressCommitted;
  const showUndoAddress = step === 'email' && addressCommitted && !emailCommitted;
  const showUndoEmail =
    emailCommitted &&
    ((step === 'residenceCard' && !residenceCardCommitted) ||
      (step === 'myNumber' && !residenceCardRequired(nationality) && !myNumberCommitted));
  const showUndoResidenceCard =
    residenceCardRequired(nationality) &&
    step === 'myNumber' &&
    residenceCardCommitted &&
    !myNumberCommitted;
  const showUndoMyNumber = step === 'maritalStatus' && myNumberCommitted && !maritalStatusCommitted;
  const showUndoMaritalStatus = step === 'familyStatus' && maritalStatusCommitted && !familyStatusCommitted;
  const showUndoFamilyStatus = step === 'disabilityCategory' && familyStatusCommitted && !disabilityCategoryCommitted;
  const showUndoDisabilityCategory =
    (step === 'disabilityFacts' && disabilityCategoryCommitted && !disabilityFactsCommitted) ||
    (step === 'emergencyContact' &&
      disabilityCategoryCommitted &&
      !needsDisabilityFacts(disabilityCategory) &&
      !emergencyContactCommitted);
  const showUndoDisabilityFacts =
    step === 'emergencyContact' &&
    needsDisabilityFacts(disabilityCategory) &&
    disabilityFactsCommitted &&
    !emergencyContactCommitted;
  const showUndoEmergencyContact =
    (step === 'emergencyName' && emergencyContactCommitted && emergencyHasContact === 'yes' && !emergencyNameCommitted) ||
    (step === 'finalConfirm' && emergencyContactCommitted && emergencyHasContact === 'no');
  const showUndoEmergencyName = step === 'emergencyRelationship' && emergencyNameCommitted && !emRelationshipCommitted;
  const showUndoEmergencyRelationship =
    step === 'emergencyPhone' && emRelationshipCommitted && !emergencyPhoneCommitted;
  const showUndoEmergencyPhone = step === 'emergencyAddress' && emergencyPhoneCommitted && !emergencyAddressCommitted;
  const showUndoEmergencyAddress =
    step === 'finalConfirm' && emergencyHasContact === 'yes' && emergencyAddressCommitted;

  const maritalStatusLabel = useMemo(() => {
    if (maritalStatus === 'married') return t('submitDocuments.profileName.maritalStatus.option.married');
    if (maritalStatus === 'not_married') return t('submitDocuments.profileName.maritalStatus.option.notMarried');
    return '';
  }, [maritalStatus, t]);

  const familyStatusDisplay = useMemo(() => {
    if (familyStatusSkipped) return t('submitDocuments.profileName.familyStatus.skipped');
    const parts: string[] = [];
    if (isWidowed) parts.push(t('submitDocuments.profileName.familyStatus.option.widowed'));
    if (isSingleParent) parts.push(t('submitDocuments.profileName.familyStatus.option.singleParent'));
    return parts.join(', ');
  }, [familyStatusSkipped, isWidowed, isSingleParent, t]);

  const disabilityCategoryLabel = useMemo(() => {
    if (disabilityCategory === 'none') return t('submitDocuments.profileName.disabilityCategory.option.none');
    if (disabilityCategory === 'general') return t('submitDocuments.profileName.disabilityCategory.option.general');
    if (disabilityCategory === 'special') return t('submitDocuments.profileName.disabilityCategory.option.special');
    return '';
  }, [disabilityCategory, t]);

  const emergencyContactLabel = useMemo(() => {
    if (emergencyHasContact === 'yes') return t('submitDocuments.profileName.emergency.yes');
    if (emergencyHasContact === 'no') return t('submitDocuments.profileName.emergency.no');
    return '';
  }, [emergencyHasContact, t]);

  const homePhoneDisplay = useMemo(() => {
    if (homePhoneSkipped || !homePhoneNumber.trim()) {
      return t('submitDocuments.profileName.homePhone.skipped');
    }
    return homePhoneNumber.trim();
  }, [homePhoneNumber, homePhoneSkipped, t]);

  const myNumberDisplay = useMemo(() => {
    if (myNumberSkipped || !normalizeMyNumber(myNumber)) {
      return t('submitDocuments.profileName.myNumber.skipped');
    }
    return maskMyNumber(myNumber);
  }, [myNumber, myNumberSkipped, t]);

  const showResidenceCardStep = residenceCardRequired(nationality);
  const showWidowedFamilyOption = maritalStatus === 'married';

  const genderLabel = useMemo(() => {
    if (gender === 'male') return t('submitDocuments.profileName.gender.option.male');
    if (gender === 'female') return t('submitDocuments.profileName.gender.option.female');
    if (gender === 'other') return t('submitDocuments.profileName.gender.option.other');
    return t('submitDocuments.profileName.gender.option.unknown');
  }, [gender, t]);

  const nationalityOptions = useMemo(
    () => [
      {
        value: 'Japanese',
        label: t('submitDocuments.profileName.nationality.option.japanese'),
        keywords: ['Japanese', 'Japan', '日本', '日本人', 'Nhật', 'Người Nhật'],
      },
      {
        value: 'Chinese',
        label: t('submitDocuments.profileName.nationality.option.chinese'),
        keywords: ['Chinese', 'China', '中国', '中国人', 'Trung Quốc', 'Người Trung Quốc'],
      },
      {
        value: 'Korean',
        label: t('submitDocuments.profileName.nationality.option.korean'),
        keywords: ['Korean', 'Korea', 'South Korea', '韓国', '韓国人', '대한민국', 'Hàn Quốc', 'Người Hàn Quốc'],
      },
      {
        value: 'Thai',
        label: t('submitDocuments.profileName.nationality.option.thai'),
        keywords: ['Thai', 'Thailand', 'タイ', 'タイ人', 'ไทย', 'Thái', 'Người Thái'],
      },
      {
        value: 'Filipino',
        label: t('submitDocuments.profileName.nationality.option.filipino'),
        keywords: ['Filipino', 'Philippines', 'フィリピン', 'フィリピン人', 'Pilipinas', 'Người Philippines'],
      },
      {
        value: 'Brazilian',
        label: t('submitDocuments.profileName.nationality.option.brazilian'),
        keywords: ['Brazilian', 'Brazil', 'ブラジル', 'ブラジル人', 'Người Brazil'],
      },
      {
        value: 'Myanmar',
        label: t('submitDocuments.profileName.nationality.option.myanmar'),
        keywords: ['Myanmar', 'Burma', 'ミャンマー', 'ミャンマー人', 'Người Myanmar'],
      },
      {
        value: 'Nepalese',
        label: t('submitDocuments.profileName.nationality.option.nepalese'),
        keywords: ['Nepalese', 'Nepal', 'ネパール', 'ネパール人', 'Người Nepal'],
      },
      {
        value: 'Sri Lankan',
        label: t('submitDocuments.profileName.nationality.option.sriLankan'),
        keywords: ['Sri Lankan', 'Sri Lanka', 'スリランカ', 'スリランカ人', 'Người Sri Lanka'],
      },
      {
        value: 'Vietnamese',
        label: t('submitDocuments.profileName.nationality.option.vietnamese'),
        keywords: ['Vietnamese', 'Vietnam', 'ベトナム', 'ベトナム人', 'Việt Nam', 'Người Việt Nam'],
      },
    ],
    [t]
  );

  const nationalityLabel = useMemo(() => {
    const match = nationalityOptions.find((o) => o.value === nationality.trim());
    return match?.label ?? nationality.trim();
  }, [nationality, nationalityOptions]);

  return {
    isLoadingProgress,
    beginEdit,
    introRevealed,
    setIntroRevealed,
    threadEndRef,
    step,
    setStep,

    legalFamily,
    setLegalFamily,
    legalFirst,
    setLegalFirst,
    legalMiddle,
    setLegalMiddle,
    kanaFamily,
    setKanaFamily,
    kanaFirst,
    setKanaFirst,
    kanaMiddle,
    setKanaMiddle,
    dob,
    setDob,
    gender,
    setGender,
    nationality,
    setNationality,
    nationalityLabel,
    phoneNumber,
    setPhoneNumber,
    homePhoneNumber,
    setHomePhoneNumber,
    homePhoneSkipped,
    setHomePhoneSkipped,
    homePhoneDisplay,
    address,
    setAddress,
    email,
    setEmail,
    residenceCardNumber,
    setResidenceCardNumber,
    myNumber,
    setMyNumber,
    myNumberSkipped,
    setMyNumberSkipped,
    maritalStatus,
    setMaritalStatus,
    familyStatusSkipped,
    setFamilyStatusSkipped,
    familyStatusSelections,
    setFamilyStatusSelections,
    isWidowed,
    isSingleParent,
    disabilityCategory,
    setDisabilityCategory,
    disabilityFacts,
    setDisabilityFacts,
    emergencyHasContact,
    setEmergencyHasContact,
    emergencyName,
    setEmergencyName,
    emRelationship,
    setEmRelationship,
    emergencyPhone,
    setEmergencyPhone,
    emergencyAddress,
    setEmergencyAddress,

    legalCommitted,
    kanaCommitted,
    dobCommitted,
    genderCommitted,
    nationalityCommitted,
    phoneCommitted,
    homePhoneCommitted,
    addressCommitted,
    emailCommitted,
    residenceCardCommitted,
    myNumberCommitted,
    maritalStatusCommitted,
    familyStatusCommitted,
    disabilityCategoryCommitted,
    disabilityFactsCommitted,
    emergencyContactCommitted,
    emergencyNameCommitted,
    emRelationshipCommitted,
    emergencyPhoneCommitted,
    emergencyAddressCommitted,

    dobDisplayFormatted,
    dobString,
    genderLabel,
    nationalityOptions,

    validateLegalName,
    validateKana,
    validatePhone,
    validateEmail,
    validateResidenceCardNumber,
    validateMyNumber,

    canSendLegal,
    canSendKana,
    canSendGender,
    canSendPhone,
    canSendHomePhone,
    canSendAddress,
    canSendEmail,
    canSendResidenceCard,
    canSendMyNumber,
    canSendMaritalStatus,
    canSendFamilyStatus,
    canSendDisabilityCategory,
    canSendDisabilityFacts,
    canSendEmergencyContact,
    canSendEmergencyName,
    canSendEmergencyRelationship,
    canSendEmergencyPhone,
    canSendEmergencyAddress,

    handleUndo,
    handleSendLegal,
    handleSendKana,
    handleSendDob,
    handleSendGender,
    handleSendNationality,
    handleSendPhone,
    handleSendHomePhone,
    handleSkipHomePhone,
    handleSendAddress,
    handleSendEmail,
    handleSendResidenceCard,
    handleSendMyNumber,
    handleSkipMyNumber,
    handleSendMaritalStatus,
    handleSendFamilyStatus,
    handleSendDisabilityCategory,
    handleSendDisabilityFacts,
    handleSendEmergencyContact,
    handleSendEmergencyName,
    handleSendEmergencyRelationship,
    handleSendEmergencyPhone,
    handleSendEmergencyAddress,
    handleProceedToSignature,
    handleContinueAfterFinalConfirm,

    showUndoLegal,
    showUndoKana,
    showUndoDob,
    showUndoGender,
    showUndoNationality,
    showUndoPhone,
    showUndoHomePhone,
    showUndoAddress,
    showUndoEmail,
    showUndoResidenceCard,
    showUndoMyNumber,
    showUndoMaritalStatus,
    showUndoFamilyStatus,
    showUndoDisabilityCategory,
    showUndoDisabilityFacts,
    showUndoEmergencyContact,
    showUndoEmergencyName,
    showUndoEmergencyRelationship,
    showUndoEmergencyPhone,
    showUndoEmergencyAddress,
    maritalStatusLabel,
    familyStatusDisplay,
    disabilityCategoryLabel,
    emergencyContactLabel,
    showWidowedFamilyOption,
    maskMyNumber,
    myNumberDisplay,
    showResidenceCardStep,
  };
}

